/** @jsx jsx */
// eslint-disable-next-line
import React from 'react'
import PropTypes from 'prop-types'
import { css, jsx } from '@emotion/core'
import Plan from './Plan'
import PlanStripe from './PlanStripe'
import formatDate from '../../../utils/formatDate'

const styles = {}
styles.container = css`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;
  margin: auto;
`

styles.descriptionContainer = css`
  display: flex;
  flex-direction: column;
  margin: 16px 0px;
  padding-left: 16px;
  font-size: 14px;
`

function Plans(props) {
  const [products, setProducts] = React.useState([])

  React.useEffect(() => {
    setProducts([...props.monthlyProducts, ...props.halfYearlyProducts, ...props.yearlyProducts])
  }, [props.monthlyProducts, props.halfYearlyProducts, props.yearlyProducts])

  // TODO: Referral field merges three different uses cases currently, we have to split it in the future.
  // - Use case 1: Champions Global. Different company that handles payments in their own merchant account.
  //   - Users can modify their subscription in Metrica, but payments are handled by Champions Global.
  // - Use case 2: BI HUB. Company that promotes Metrica and get a commission for it.
  //   - Users can modify their subscription in Metrica, payments are handled by Metrica.
  // - Use case 3: SportLander. Different company that buy licenses and resell them to their customers.
  //   - Users cannot modify their subscription in Metrica, payments are handled by SportLander.
  const sportLanderId = 'sport_lander'
  const ownSubscriptions = props.subscriptions.filter(v => {
    return (
      v.order &&
      v.order.email === props.user.email &&
      v.order.referral !== sportLanderId &&
      // Hide subscriptions with an unknown or private product (e.g. multi-year, academic, etc.).
      v.order.items
        .filter(w => w.user_key && w.user_key.licence_configuration && w.user_key.licence_configuration.base_template)
        .every(w => products.some(p => p.priceId === w.id && !p.isPrivate))
    )
  })

  const showStripeSubscriptions =
    props.subscriptionInUse &&
    props.subscriptionInUse.order &&
    !props.subscriptionInUse.order.merchant

  const isExternalSubscription =
    props.subscriptionInUse &&
    props.subscriptionInUse.order &&
    (props.subscriptionInUse.order.email !== props.user.email ||
      props.subscriptionInUse.order.referral === sportLanderId)

  return (
    <React.Fragment>
      {
        // External subscription.
        isExternalSubscription && (
          <div css={styles.descriptionContainer}>
            <span
              css={styles.title}
              dangerouslySetInnerHTML={{
                __html: window.I18n.t('settings.noOwner', {
                  pre: '<b>',
                  email:
                    props.subscriptionInUse.order.referral === sportLanderId
                      ? 'SportLander'
                      : props.subscriptionInUse.order.email,
                  post: '</b>'
                })
              }}
            />
            <span css={styles.description}>
              {props.subscriptionInUse.order.pause_at && (
                <span>
                  <span css={styles.infoPlanDescription}>{window.I18n.t('settings.pauseAt')}</span>
                  <span> </span>
                  <span css={styles.fontBolder}>
                    {formatDate(props.subscriptionInUse.order.pause_at)}
                  </span>
                </span>
              )}

              {props.subscriptionInUse.order.cancel_at && (
                <span>
                  <span css={styles.infoPlanDescription}>{window.I18n.t('settings.cancelAt')}</span>
                  <span> </span>
                  <span css={styles.fontBolder}>
                    {formatDate(props.subscriptionInUse.order.cancel_at)}
                  </span>
                </span>
              )}

              {props.subscriptionInUse.order.renewal_at && (
                <span>
                  <span css={styles.infoPlanDescription}>{window.I18n.t('settings.renewAt')}</span>
                  <span> </span>
                  <span css={styles.fontBolder}>
                    {formatDate(props.subscriptionInUse.order.renewal_at)}
                  </span>
                </span>
              )}
            </span>
          </div>
        )
      }

      <div css={styles.container}>
        {
          // Stripe subscriptions.
          showStripeSubscriptions &&
          ownSubscriptions.map(subscription => (
            <PlanStripe key={subscription.order._id} subscription={subscription} />
          ))
        }

        {
          // Free subscription.
          !isExternalSubscription && ownSubscriptions.length === 0 && (
            <Plan
              paddle={props.paddle}
              products={products}
              monthlyProducts={props.monthlyProducts}
              halfYearlyProducts={props.halfYearlyProducts}
              yearlyProducts={props.yearlyProducts}
              subscription={{}}
              userIp={props.userIp}
              user={props.user}
              customer={props.customer}
              address={props.address}
            />
          )
        }

        {
          // Own Paddle subscriptions.
          !showStripeSubscriptions &&
          ownSubscriptions.map(subscription => {
            // Don't show Stripe subscriptions.
            if (subscription.order && !subscription.order.merchant) {
              return null
            }

            return (
              <Plan
                key={subscription.order ? subscription.order._id : subscription.planName}
                paddle={props.paddle}
                products={products}
                monthlyProducts={props.monthlyProducts}
                halfYearlyProducts={props.halfYearlyProducts}
                yearlyProducts={props.yearlyProducts}
                subscription={subscription}
                userIp={props.userIp}
                user={props.user}
                customer={props.customer}
                address={props.address}
              />
            )
          })
        }
      </div>
    </React.Fragment>
  )
}

Plans.defaultProps = {
  paddle: undefined,
  subscriptionInUse: undefined,
  customer: undefined,
  address: undefined
}

Plans.propTypes = {
  paddle: PropTypes.object,
  userIp: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired,
  subscriptionInUse: PropTypes.object,
  subscriptions: PropTypes.array.isRequired,
  customer: PropTypes.object,
  address: PropTypes.object,
  monthlyProducts: PropTypes.array.isRequired,
  halfYearlyProducts: PropTypes.array.isRequired,
  yearlyProducts: PropTypes.array.isRequired
}

export default Plans
